import { Component, ElementRef, Input, OnInit, Renderer2 as Renderer } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CmdConnextService,
  ConnextData,
  GenericDeviceValidator,
  MapService,
  MonitorPrefEnumType,
  ProductModelService,
  StateService,
  System,
  SystemsService,
  UserPermissions,
  checkFwVersion,
  isAdmin,
  isAuthorized,
  isConnected,
  isNotNullOrUndefined,
  isOwner,
} from 'ngx-core-services';
import { UiPromiseService, ToastFacade, DialogDataComponent } from 'ngx-zcs-components';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, combineLatest, from, of } from 'rxjs'

import { POWER_VALIDATOR } from '../create-firebase/create.utils';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectedSystem, selectedSystemConnextData, selectedSystemMetadata, SystemDefinition, SystemsState } from '../../store';
import { ICustomBlock } from '../decorators';
import { ConnextConfiguration, virtualConfiguration } from '../configuration-section/configuration-section.models';
import { ConnextUsersForm } from '../users-section/users-section-models';
import { PositionDetails } from '../components/maps-form/maps-form.models';
import { labels } from '../create-block/create.model';
import { updateFormControlIfDifferent } from '../settings-block/settings-block.utils';

export type SystemConfig = {
  type: 'connext';
  system: SystemDefinition;
  connextConfig: Partial<ConnextData>;
  userLevel: UserPermissions;
} | {
  type: 'virtual';
  system: SystemDefinition;
};

export type SettingsPageConfig = {
  system: SystemDefinition;
  virtualConfiguration: virtualConfiguration;
  connextConfiguration: ConnextConfiguration;
  connextUsers: ConnextUsersForm;
  isConnextLoggerSelected: boolean;
  isConnextLoggerOwner: boolean;
  isConnextLoggerAdmin: boolean;
  isConnextOffline: boolean;
  connextData?: Partial<ConnextData>;
  connextSerial?: string;
  connextMeterConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  connextSensorConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  connextExternalConfig?: FormGroup<{ consider: FormControl<number | null> }>;
};

@UntilDestroy()
@CreateBlockReduxSupport()
@Component({
  selector: 'settings-firebase-block',
  templateUrl: './settings-firebase.block.html',
  styleUrls: ['./settings-firebase.block.scss'],
})
export class SettingsFirebaseBlock implements ICustomBlock, IReduxBlock, OnInit {

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof labels{
    return this._labels;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  virtualConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    meterInversion: new FormControl<boolean | null | undefined>(null),
  });

  connextConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    plantPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl('', Validators.required),
  });

  connextUsers = new FormGroup({
    users: new FormArray([
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        userLevel: new FormControl(UserPermissions.Admin, Validators.required),
        isSelected: new FormControl(false),
        added: new FormControl(false, Validators.requiredTrue),
      }),
    ]),
    isModified: new FormControl(false),
  });

  connextMeterConfig = new FormGroup({
    // -1: inverted, 1: normal
    // validator only null, -1, 1
    sign: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^-1|1$/)]),
  });

  connextSensorConfig = new FormGroup({
    // -1: inverted, 1: normal
    sign: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^-1|1$/)]),
  });

  connextExternalConfig = new FormGroup({
    // 0: do not consider, 1: consider
    consider: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^0|1$/)]),
  });

  readonly connextData = this.store.select(selectedSystemConnextData);

  readonly selectedSystem$ = this.store.select(selectedSystem).pipe(isNotNullOrUndefined());

  readonly systemMetadata$ = this.store.select(selectedSystemMetadata).pipe(isNotNullOrUndefined());

  readonly updateConfiguration$: Observable<SettingsPageConfig | null | undefined> = combineLatest([
    this.selectedSystem$,
    this.connextData,
    this._refresh$,
  ]).pipe(
    switchMap(([system, connextData]) => {
      if (!system || !system.monitorPref) {
        return [];
      }

      return system.monitorPref === MonitorPrefEnumType.CONNEXT
        ? this.updateConnextConfiguration(system, connextData!)
        : this.updateVirtualOrConnextWithoutAuthConfiguration(system)

    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly showMeterInversion$ = this.systemMetadata$.pipe(
    map((metadata) => metadata.invertMeterSensor !== undefined),
  );

  readonly updateFormControlIfDifferentByConnextForm = updateFormControlIfDifferent(this.connextConfiguration);
  readonly updateFormControlIfDifferentByVirtualForm = updateFormControlIfDifferent(this.virtualConfiguration);

  constructor(
    private readonly cmdConnextService: CmdConnextService,
    private readonly dialog: MatDialog,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly mapService: MapService,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly stateService: StateService,
    readonly systemsService: SystemsService,
  ) {
    this.updateSystemOnFirebase = this.updateSystemOnFirebase.bind(this);
  }

  ngOnInit() {
    combineLatest([
      this.systemsService.systems$,
      this.route.params,
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([systems, params]) => {
      const uid = params.uid;
      const systemsNotValid = systems?.filter((system) => system.uid !== uid);
      this.virtualConfiguration.controls.plantName.setAsyncValidators([
        this.genericDeviceValidator.deviceUnique(
          systemsNotValid,
          async (value) => value,
          'name',
        ),
      ]);

      this.connextConfiguration.controls.plantName.setAsyncValidators([
        this.genericDeviceValidator.deviceUnique(
          systemsNotValid,
          async (value) => value,
          'name',
        ),
      ]);
    });
  }

  async updateVirtualOrConnextWithoutAuthConfiguration(
    system: SystemDefinition,
    withConnextButWithoutAuthorization = false,
  ): Promise<SettingsPageConfig> {
    const plantName = system.name;
    const meterInversion = system.metadata?.invertMeterSensor;
    const position = {
      lat: system.metadata?.location?.lat ? +(system.metadata.location.lat) : 0,
      lng: system.metadata?.location?.lng ? +(system.metadata.location.lng) : 0,
    }

    const latLng = await this.mapService.getLatLng(position);

    this.updateFormControlIfDifferentByVirtualForm({
      plantName,
      coordinates: {
        latLng,
        timezone: null,
      },
      meterInversion: meterInversion ?? null,
    });

    /**
     * If the user has no authorization to edit the system, we disable the coordinates field
     */
    if (withConnextButWithoutAuthorization) {
      this.virtualConfiguration.controls.coordinates.disable();
    }

    if (withConnextButWithoutAuthorization) {
      return {
        system,
        virtualConfiguration: this.virtualConfiguration,
        connextConfiguration: this.connextConfiguration,
        connextUsers: this.connextUsers,
        isConnextLoggerSelected: true,
        isConnextLoggerOwner: false,
        isConnextLoggerAdmin: false,
        /**
         * If the user has no authorization to edit the system, consider connext always online
         */
        isConnextOffline: false,
        connextSerial: system.configuration.connextId as string,
      };
    }

    return {
      system,
      virtualConfiguration: this.virtualConfiguration,
      connextConfiguration: this.connextConfiguration,
      connextUsers: this.connextUsers,
      isConnextLoggerSelected: false,
      /**
       * If the user has no authorization to edit the system, consider connext always online
       */
      isConnextOffline: false,
      isConnextLoggerOwner: false,
      isConnextLoggerAdmin: false,
    };
  }

  async updateConnextConfiguration(system: SystemDefinition, connextConfig: Partial<ConnextData>): Promise<SettingsPageConfig> {
    const plantName = system.name;
    const uid = system.configuration.connextId as string;
    const lat = connextConfig?.config?.latitude || 0;
    const lng = connextConfig?.config?.longitude || 0;

    const hasAdminAuthorization = isAuthorized(connextConfig);
    if (!hasAdminAuthorization) {
      const updatedSystem: SystemDefinition = {
        ...system,
        configuration: {
          activeZone: {
            coordinates: `${lat}, ${lng}`,
            tz: system.configuration.activeZone?.tz || 0,
          },
          ...system.configuration,
        },
      };
      const disableLocation = true;
      return this.updateVirtualOrConnextWithoutAuthConfiguration(updatedSystem, disableLocation);
    }

    const sensor_mode = connextConfig?.config?.sensor_mode;
    const devices = connextConfig?.devices || [];
    const thereAreThreePhases = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'inverter' && product.type.phase === 'three-phase';
    });
    const thereAreNoZCS = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'no-zcs';
    });
    const thereAreHybridInverters = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'inverter' && (product.type.value === 'hybrid-3ph' || product.type.value === 'hybrid');
    });

    /**
     * (SENS2_K CONNEXT << 4 | SENS1_METER	), // 0
     * (SENS2_CONNEXT  << 4 | SENS1_CONNEXT),	 // 1
     * (SENS2_INVERTER << 4 | SENS1_CONNEXT),	 // 2
     * (SENS2_INVERTER << 4 | SENS1_METER  ),	 // 3
     * (SENS2_INVERTER << 4 | SENS1_INVERTER), // 4 || 3
     * (SENS2_INVERTER << 4 | SENS1_METER)		// 5
     *
     * If production sensor is enabled, we block the phase selection,
     * same thing if there are three phases inverters
     */
    const phaseBlocked = (['0', '1'].includes(`${sensor_mode}`) && thereAreNoZCS) || thereAreThreePhases;
    const phase = connextConfig?.config?.pType || 0;
    const power = (connextConfig?.config?.maxLoadPwr || 0) * 100;
    const latLng = await this.mapService.getLatLng({
      lat,
      lng,
    });

    this.updateFormControlIfDifferentByConnextForm({
      plantName,
      coordinates: {
        latLng,
        timezone: null,
      },
      plantPower: power,
      plantType: phase,
      applied: true,
      plantUid: uid,
    });

    if (phaseBlocked) {
      this.connextConfiguration.controls.plantType.disable();
    }

    const settingsConfig: SettingsPageConfig = {
      system,
      connextData: connextConfig,
      virtualConfiguration: this.virtualConfiguration,
      connextConfiguration: this.connextConfiguration,
      connextUsers: this.connextUsers,
      isConnextLoggerSelected: true,
      isConnextLoggerOwner: isOwner(connextConfig),
      isConnextLoggerAdmin: isAdmin(connextConfig),
      isConnextOffline: !isConnected(connextConfig),
      connextSerial: system.configuration.connextId as string,
      connextMeterConfig: undefined,
      connextSensorConfig: undefined,
      connextExternalConfig: undefined,
    };

    const connextFwVersion = connextConfig?.info?.fw.fw_ver;
    const supportChangeSensorSignAndExternalConfig = checkFwVersion(connextFwVersion, '1.40.0000');
    if (sensor_mode !== 4 && supportChangeSensorSignAndExternalConfig) {
      if (phase === 1) {
        this.updateFormControlIfDifferentByConnextForm({
          sign: connextConfig?.config?.mt_sign as number,
        });
        settingsConfig.connextMeterConfig = this.connextMeterConfig;
      } else {
        this.updateFormControlIfDifferentByConnextForm({
          sign: connextConfig?.config?.ta_sign as number,
        });
        settingsConfig.connextSensorConfig = this.connextSensorConfig;
      }
    }

    if (thereAreHybridInverters && supportChangeSensorSignAndExternalConfig) {
      this.updateFormControlIfDifferentByConnextForm({
        consider: connextConfig?.config?.pext as number,
      });
      settingsConfig.connextExternalConfig = this.connextExternalConfig;
    }

    /**
     * Users section
     */
    const users = connextConfig?.users || [];
    if (users.length) {
      this.connextUsers.controls.users.clear();
      users.forEach((user) => {
        this.connextUsers.controls.users.push(new FormGroup({
          email: new FormControl(user.email, [Validators.required, Validators.email]),
          userLevel: new FormControl(user.userLevel, Validators.required),
          isSelected: new FormControl(false),
          added: new FormControl(true),
        }));
      });

      this.connextUsers.controls.users.push(new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        userLevel: new FormControl<number>(UserPermissions.Admin, Validators.required),
        isSelected: new FormControl(false),
        added: new FormControl(false),
      }));

      this.connextUsers.controls.isModified.setValue(false);
    }

    return settingsConfig;
  }

  async updateSystemOnFirebase(system: SystemDefinition) {
    this.uiPromiseService.run(async () => {
      const isConnextConnected = isConnected(system.data as Partial<ConnextData>);
      const plantNameControl = (system.configuration.connextId && isConnextConnected) ? this.connextConfiguration.controls.plantName : this.virtualConfiguration.controls.plantName;
      const coordinatesControl = (system.configuration.connextId && isConnextConnected) ? this.connextConfiguration.controls.coordinates : this.virtualConfiguration.controls.coordinates;
      const meterInversionControl = this.virtualConfiguration.controls.meterInversion;

      if (plantNameControl.invalid || coordinatesControl.invalid) {
        return;
      }

      const plantName = plantNameControl.value;
      const coordinates = coordinatesControl.value;

      if ( !coordinates || !coordinates.latLng || !plantName ) {
        return;
      }

      if (!coordinates.timezone) {
        const timezone = await this.mapService.getTz(coordinates.latLng);
        if (!timezone) {
          return;
        }
        coordinates.timezone = timezone;
      }

      const tz = (Number(coordinates.timezone?.gmtOffset) / 3600) * 4;
      const locationLat = `${coordinates.latLng.lat().toPrecision(7)}`;
      const locationLng = `${coordinates.latLng.lng().toPrecision(7)}`;
      const updatedSystem: System = {
        uid: system.uid,
        img: system.img,
        source: 'firebase',
        devices: system.devices,
        name: plantName,
        configuration: {
          ...system.configuration,
          activeZone: {
            ...system.configuration.activeZone,
            coordinates: `${locationLat}, ${locationLng}`,
            tz,
          },
          timezoneEnd: coordinates.timezone?.zoneEnd,
        },
      };

      if (meterInversionControl.value !== null && meterInversionControl.value !== undefined) {
        updatedSystem.configuration = {
          ...updatedSystem.configuration,
          invertMeterSensor: meterInversionControl.value,
        };
      }

      await this.stateService.saveCurrentSystem(updatedSystem);
    },
    {
      loading: true,
    });
  }

  async deleteSystem(config: SettingsPageConfig) {
    if (config.system.configuration.connextId) {
      return this.deleteConnextSystem(config);
    }

    return this.deleteVirtualSystem(config);
  }

  async deleteConnextSystem(config: SettingsPageConfig) {
    if (!config.isConnextLoggerOwner) {
      return this.deleteVirtualSystem(config);
    }

    if (!isConnected(config.connextData)) {
      return this.toastFacade.present(
        this.labels.connextOfflineToastContent,
        this.labels.connextOfflineToastTitle,
        {
          type: 'browser',
          browserMode: 'persistent',
          severity: 'error',
        },
      );
    }
    const connextId = config.system.configuration.connextId as string;

    const primaryActionFn = async () => {
      const noAutoOwner = false;
      const advancedMode = false;
      const cmd = 'set default all';
      await this.cmdConnextService.sendCommand(connextId, cmd, noAutoOwner, advancedMode);
      this.uiPromiseService.run(() => this.deleteSystemByFirebase(config), {
        loading: true,
        loadingMessage: this.labels.deletingPlant,
      });
    };

    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      this.labels.deletePlantDialogContentWithConnext,
      primaryActionFn,
    );
  }

  async deleteVirtualSystem(config: SettingsPageConfig) {
    const primaryActionFn = () => this.uiPromiseService.run(() => this.deleteSystemByFirebase(config), {
      loading: true,
      loadingMessage: this.labels.deletingPlant,
    });

    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      this.labels.deletePlantDialogContentVirtual,
      primaryActionFn,
    );
  };

  async deleteSystemByFirebase(config: SettingsPageConfig) {
    const uid = config.system.uid as string;

    await this.stateService.removeCurrentSystem(uid);
    await this.toastFacade.present(
      this.labels.deletePlantToastContent,
      this.labels.deletePlantToastTitle,
      {
        type: 'browser',
        browserMode: 'persistent',
        severity: 'success',
      },
    );
  }

  async openDialogToDeleteSystem(title: string, content: string, primaryActionFn: () => Promise<any>) {
    const dialogRef = this.dialog.open(DialogDataComponent, {
      width: '600px',
    });
    const actions = [
      {
        text: this.labels.cancel,
        action: () => dialogRef.close(),
      },
      {
        text: this.labels.deletePlantDialogAction,
        cssClass: 'custom-mat-button-warning',
        focus: true,
        action: () => {
          primaryActionFn();
          dialogRef.close();
        },
      },
    ];

    dialogRef.componentInstance.data = {
      title,
      content,
      buttons: actions,
    };
  }
}
